import * as React from 'react';
import { storyblokEditable, StoryblokComponent } from '@storyblok/react';
import { StoryblokRichText, type SbBlokData } from '@storyblok/react';

import type { CalloutStoryblok } from '../../types/storyblok';

interface CalloutBlokProps {
  blok: CalloutStoryblok;
}

type Background = 'default' | 'muted' | 'accent';
type Align = 'center' | 'start';

// Schema option fields emit an empty-string sentinel alongside the authored
// values (Storyblok CLI pattern; see Button.tsx for the canonical resolver
// shape). Coerce `''` and `undefined` to the schema's authored defaults at
// render time: `accent` for background, `center` for align (R2.5–R2.7).
const resolveBackground = (value: CalloutStoryblok['background']): Background => {
  if (value === 'default' || value === 'muted' || value === 'accent') return value;
  return 'accent';
};

const resolveAlign = (value: CalloutStoryblok['align']): Align => {
  if (value === 'center' || value === 'start') return value;
  return 'center';
};

// R2.5 — `accent` is transparent so the Callout inherits its parent's deep-blue
// surface. Post-pivot the callout lives inside the footer (which already
// applies `bg-brand-deep`); painting its own deep-blue would create a redundant
// surface layer and risk z-index/seam issues. Text-color inversion to
// `text-neutral-white` is applied separately so the callout's foreground
// remains legible against the inherited deep-blue.
const backgroundClass: Record<Background, string> = {
  default: '',
  muted: 'bg-gray-50',
  accent: '',
};

// R2.7 — section-level alignment governs both text-axis (`text-*`) and the
// flex cross-axis (`items-*`) so the inner column (eyebrow / headline / body /
// CTA cluster) tracks the chosen alignment together. Per R3.5, the CTA cluster
// inherits this alignment rather than carrying a per-button override.
const alignClass: Record<Align, string> = {
  center: 'text-center items-center',
  start: 'text-left items-start',
};

// R2.4 — the body element is suppressed when the rich-text document is
// structurally absent OR visually empty. Storyblok rich-text payloads are
// rarely `undefined` even when the editor leaves the field blank — they
// arrive as a populated document object with empty paragraph nodes. Naive
// truthiness would render an empty `<div>` (whitespace placeholder) and
// violate R2.4. See design.md → "Empty Rich-Text Definition" for the five
// fixture variants this helper covers.
const hasBodyContent = (doc: CalloutStoryblok['body']): boolean => {
  if (!doc) return false;
  const content = doc.content;
  if (!content || content.length === 0) return false;
  return content.some((node) => {
    if (node.type !== 'paragraph') return true;
    const children = node.content;
    if (!children || children.length === 0) return false;
    return children.some(
      (child) =>
        child.type !== 'text' || (typeof child.text === 'string' && child.text.trim().length > 0)
    );
  });
};

export const Callout = ({ blok }: CalloutBlokProps): JSX.Element => {
  const resolvedBackground = resolveBackground(blok.background);
  const resolvedAlign = resolveAlign(blok.align);
  const showBody = hasBodyContent(blok.body);
  const ctaCount = blok.cta?.length ?? 0;

  // R2.5 — text-color inversion when the section surface is deep-blue, so
  // foreground content remains legible against the accent background. Default
  // / muted surfaces inherit the existing text-color tokens (no override).
  const textColorClass = resolvedBackground === 'accent' ? 'text-neutral-white' : '';

  const sectionClass = [
    'relative overflow-hidden',
    backgroundClass[resolvedBackground],
    textColorClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section {...storyblokEditable(blok as unknown as SbBlokData)} className={sectionClass}>
      {/*
       * Inner alignment container — mirrors the MediaText / Footer layout
       * convention. Max-width + horizontal padding align the callout content
       * with surrounding section blocks; vertical rhythm uses the compact
       * `py-12 desktop:py-16` (R2.9). The wave is the footer's responsibility
       * (the callout composes inside the footer's deep-blue zone), so this
       * component does not render a wave decoration of its own.
       */}
      <div
        className={`relative max-w-[1110px] mx-auto px-grid-margin-mobile desktop:px-grid-margin-desktop py-12 desktop:py-16 flex flex-col ${alignClass[resolvedAlign]}`}
      >
        {/* R2.3 — eyebrow rendered only when the authored string is non-empty. */}
        {blok.eyebrow && (
          <p className="text-callout font-extrabold uppercase tracking-[0.16em]">{blok.eyebrow}</p>
        )}

        {/* R2.2 — headline is required and renders as the section's `<h2>`. */}
        <h2 className="text-h5 font-bold mt-2">{blok.headline}</h2>

        {/* R2.4 — body element is suppressed entirely on empty (see hasBodyContent). */}
        {showBody && (
          <div className="text-body1 mt-4">
            <StoryblokRichText
              doc={blok.body as unknown as Parameters<typeof StoryblokRichText>[0]['doc']}
            />
          </div>
        )}

        {/*
         * R3.1 — CTA dispatch via `<StoryblokComponent>` so each `button`
         * entry resolves through the registry (R3.2 — embedded behaviour is
         * identical to top-level `button` blok). R3.3 omits the cluster when
         * the array is empty/undefined. R3.5 — alignment is governed by the
         * section-level flex root above; the cluster itself uses
         * `flex-wrap gap-4` for graceful multi-button wrapping.
         */}
        {ctaCount > 0 && (
          <div className="flex flex-wrap gap-4 mt-6">
            {blok.cta!.map((entry) => (
              <StoryblokComponent key={entry._uid} blok={entry as unknown as SbBlokData} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
